import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [payStatus, setPayStatus] = useState('completed');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        payStatus: payStatus
      };
      
      const response = await apiService.post('orderListV2', params);
      if (response && response.data) {
        setOrders(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load orders');
      console.error('Order fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, payStatus]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrders();
    }, keyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchOrders]);

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Order List</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setCurrentPage(1); }}
                placeholder="Enter your search query here.."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <select
              value={payStatus}
              onChange={(e) => { setPayStatus(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="All">Show All</option>
              <option value="pending">Pending</option>
              <option value="completed">Complete</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-cart-check text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Orders Found</h3>
              <p className="text-gray-500">No orders match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">Info</th>
                    <th className="px-4 py-3">Credit</th>
                    <th className="px-4 py-3">TotalAmount</th>
                    <th className="px-4 py-3">Pay Status</th>
                    <th className="px-4 py-3">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order, index) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3 text-sm">{startIndex + index + 1}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        {order.userId ? (
                          <div className="text-xs">
                            <div className="font-semibold text-[#009ef7]">{order.userId.first_name} {order.userId.last_name || ''}</div>
                            {order.userId.business_name && (
                              <Link to={`/vendorWallet/${order.userId._id}`} className="text-[#009ef7] hover:text-[#009ef7] no-underline">
                                {order.userId.business_name.charAt(0).toUpperCase() + order.userId.business_name.slice(1)}
                              </Link>
                            )}
                            <div className="text-gray-500">{order.userId.email || ''}</div>
                            <div className="text-gray-500">{order.userId.phone || ''}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No User</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="border rounded p-2 text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500">Order Id:</span>
                            <span>{order.orderId}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Transaction Id:</span>
                            <span>{order.paymentId}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        {order.amount !== null && order.amount !== undefined ? `₹${order.amount}` : '--'}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        {order.amount} + {order.gst} = ₹{order.amount + order.gst}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs text-white ${order.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{formatDate(order.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {orders.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-700">Show</span>
              <select
                value={recordsPerPage}
                onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >«</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className="px-3 py-1 border rounded text-xs sm:text-sm"
                      style={{
                        backgroundColor: currentPage === page ? '#009ef7' : '#fff',
                        borderColor: currentPage === page ? '#009ef7' : '#d1d5db',
                        color: currentPage === page ? '#fff' : '#374151'
                      }}
                    >{page}</button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderList;
